import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, X, Search } from "lucide-react";
import useClickOutside from "../../hooks/useClickOutside";

/**
 * Reusable multi-select. Selected items render as removable chips INSIDE the
 * input-like box (not stacked above it). Opens a checkbox dropdown.
 *
 * Props:
 *   options        [{ value, label, suffix? }]   suffix = optional small node after the label (e.g. a "Voice" tag)
 *   value          array of selected values
 *   onChange       (nextValues) => void
 *   placeholder    text shown when nothing is selected
 *   loading        bool — disables and shows a loading label
 *   emptyText      text shown in the dropdown when there are no options
 *   enableSelectAll bool — show a "Select all" row at the top
 *   disabled       bool
 *   maxChips       collapse chips beyond this count into "+N more" (default 6)
 *   searchable     bool — show a type-to-filter box at the top of the dropdown (default true)
 *   searchPlaceholder  placeholder for the search box
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  loading = false,
  emptyText = "No options available",
  enableSelectAll = false,
  disabled = false,
  maxChips = 6,
  searchable = true,
  searchPlaceholder = "Search…",
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const searchInputRef = useRef(null);
  const closeDropdown = () => {
    setOpen(false);
    setQuery("");
  };
  useClickOutside(ref, closeDropdown, open);

  // Focus the search box only once the dropdown is actually open. The panel is
  // always mounted (visibility is toggled via CSS), so a bare `autoFocus` would
  // steal focus on mount and scroll the page to this hidden input.
  useEffect(() => {
    if (open) searchInputRef.current?.focus();
  }, [open]);

  const selectedOptions = value
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean);
  const visibleChips = expanded
    ? selectedOptions
    : selectedOptions.slice(0, maxChips);
  const hiddenCount = selectedOptions.length - visibleChips.length;

  const toggle = (val) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  // Options matching the current search query (label-based, case-insensitive).
  const q = query.trim().toLowerCase();
  const filtered =
    searchable && q
      ? options.filter((o) => String(o.label).toLowerCase().includes(q))
      : options;

  // "Select all" acts on whatever is currently visible (filtered) set.
  const filteredValues = filtered.map((o) => o.value);
  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every((v) => value.includes(v));

  return (
    <div ref={ref} className="relative">
      <div
        role="button"
        tabIndex={0}
        aria-disabled={disabled || loading}
        onClick={() => {
          if (disabled || loading) return;
          open ? closeDropdown() : setOpen(true);
        }}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled && !loading) {
            e.preventDefault();
            open ? closeDropdown() : setOpen(true);
          }
        }}
        className={`flex min-h-[38px] w-full items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1.5 text-sm shadow-sm transition focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary ${
          disabled || loading
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:bg-muted/40"
        }`}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {loading ? (
            <span className="text-muted-foreground">Loading…</span>
          ) : selectedOptions.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map((o) => (
                <span
                  key={o.value}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground border border-border"
                >
                  {o.label}
                  {o.suffix}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(o.value);
                    }}
                    className="ml-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  className="rounded-full px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/5"
                >
                  +{hiddenCount} more
                </button>
              )}
            </>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      <div
        className={`absolute z-20 mt-1 w-full rounded-lg border border-border bg-card shadow-lg transition-all ${
          open
            ? "opacity-100 translate-y-0"
            : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        {options.length === 0 ? (
          <div className="px-4 py-2 text-sm text-muted-foreground">
            {emptyText}
          </div>
        ) : (
          <>
            {searchable && (
              <div className="border-b border-border p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full rounded-md border border-input bg-background py-1.5 pl-7 pr-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            )}
            <div className="max-h-60 overflow-y-auto py-1">
              {enableSelectAll && filtered.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    onChange(
                      allFilteredSelected
                        ? value.filter((v) => !filteredValues.includes(v))
                        : [...new Set([...value, ...filteredValues])],
                    )
                  }
                  className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-sm font-medium hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={allFilteredSelected}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          filteredValues.some((v) => value.includes(v)) &&
                          !allFilteredSelected;
                    }}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                  />
                  {q ? "Select all matches" : "Select all"}
                </button>
              )}
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No matches
                </div>
              ) : (
                filtered.map((o, i) => {
                  const showGroup = o.group && o.group !== filtered[i - 1]?.group;
                  return (
                    <React.Fragment key={o.value}>
                      {showGroup && (
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {o.group}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => toggle(o.value)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={value.includes(o.value)}
                          className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                        />
                        <span className="text-foreground">{o.label}</span>
                        {o.suffix}
                      </button>
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
